<script setup>
import ItemSelect from '@/Components/ItemSelect.vue';
import TextField from '@/Components/TextField.vue';
import TextareaField from '@/Pages/Order/TextareaField.vue';
import FileInputField from '@/Components/FileInputField.vue';
import { computed, ref, watch } from 'vue';
import LocationSelect from '@/Components/LocationSelect.vue';
import BinSelect from '@/Components/BinSelect.vue';
import BatchSelect from '@/Components/BatchSelect.vue';
import InventoryTable from '@/Pages/Ticket/Partials/InventoryTable.vue';

const props = defineProps({
    subtype: {
        type: Object,
        default: null,
    },
    index: {
        type: Number,
        default: null,
    },
    createTicketForm: {
        type: Object,
        required: true,
    },
    ticket: {
        type: [Object, null],
        default: null,
    },
});

const form = defineModel({
    type: Object,
    required: true,
});

const errorKeyPrefix = computed(() => {
    if (props.index === null) {
        return '';
    }
    return `content_lines.${props.index}.`;
});

const item1 = ref(null);

const inventoryLoading = ref(false);
const inventoryRows = ref([]);
watch(
    () => props.subtype,
    async (subtype) => {
        if (subtype.inventory_label !== null) {
            inventoryLoading.value = true;
            const response = await axios.get(
                route('api.ticket.content.inventory.show', {
                    ticket: props.ticket.id,
                }),
            );
            inventoryRows.value = response.data;
            inventoryLoading.value = false;
        }
    },
);
</script>

<template>
    <v-card v-if="createTicketForm" class="tw-p-4">
        <v-row v-if="subtype">
            <v-col cols="12">
                <span
                    v-html="
                        $t('ticket.content_description', {
                            subtype: subtype.name,
                        })
                    "
                ></span>
            </v-col>
        </v-row>
        <v-row v-if="subtype">
            <v-col cols="12">
                <v-row>
                    <v-col v-if="subtype.location_code_label !== null" cols="4">
                        <LocationSelect
                            v-model="form.location_code"
                            :label="subtype.location_code_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'location_code'
                                ]
                            "
                        ></LocationSelect>
                    </v-col>
                    <v-col v-if="subtype.bin_code_label !== null" cols="6">
                        <BinSelect
                            v-model="form.bin_code"
                            :location_code="form.location_code"
                            :label="subtype.bin_code_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'bin_code'
                                ]
                            "
                        ></BinSelect>
                    </v-col>
                    <v-col v-if="subtype.text_label !== null" cols="6">
                        <TextField
                            v-model="form.text"
                            :label="subtype.text_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[errorKeyPrefix + 'text']
                            "
                        ></TextField>
                    </v-col>
                    <v-col
                        v-if="subtype.external_shipment_id_label !== null"
                        cols="6"
                    >
                        <TextField
                            v-model="form.external_shipment_id"
                            :label="subtype.external_shipment_id_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'external_shipment_id'
                                ]
                            "
                        ></TextField>
                    </v-col>
                    <v-col v-if="subtype.item_number_1_label !== null" cols="4">
                        <ItemSelect
                            v-model="form.item_number_1"
                            :label="subtype.item_number_1_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'item_number_1'
                                ]
                            "
                        ></ItemSelect>
                    </v-col>
                    <v-col v-if="subtype.batch_label !== null" cols="4">
                        <BatchSelect
                            v-model="form.batch"
                            class="!tw-mb-0"
                            :label="subtype.batch_label"
                            :item-number="form.item_number_1"
                            :disabled="!form.item_number_1"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'batch'
                                ]
                            "
                        ></BatchSelect>
                    </v-col>
                    <v-col v-if="subtype.item_number_2_label !== null" cols="4">
                        <ItemSelect
                            v-model="form.item_number_2"
                            v-model:item="item1"
                            :label="subtype.item_number_2_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'item_number_2'
                                ]
                            "
                        ></ItemSelect>
                    </v-col>

                    <v-col v-if="subtype.quantity_label !== null" cols="3">
                        <TextField
                            v-model="form.quantity"
                            :label="subtype.quantity_label"
                            hide-details="auto"
                            type="number"
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'quantity'
                                ]
                            "
                        ></TextField>
                    </v-col>
                    <v-col v-if="subtype.note_label !== null" cols="12">
                        <TextareaField
                            v-model="form.note"
                            :label="subtype.note_label"
                            hide-details="auto"
                            :error-messages="
                                createTicketForm.errors[errorKeyPrefix + 'note']
                            "
                        ></TextareaField>
                    </v-col>
                    <v-col v-if="subtype.attachments_label !== null" cols="12">
                        <FileInputField
                            v-model="form.attachments"
                            :label="subtype.attachments_label"
                            multiple
                            :error-messages="
                                createTicketForm.errors[
                                    errorKeyPrefix + 'attachments'
                                ]
                            "
                        ></FileInputField>
                    </v-col>
                    <v-col v-if="subtype.inventory_label !== null" cols="12">
                        <InventoryTable
                            v-model="form.inventory"
                            :rows="inventoryRows"
                            :loading="inventoryLoading"
                        ></InventoryTable>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-card>
</template>

<style scoped></style>
